import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FundListDetail } from '@nemo/api';
import { useCallback } from 'react';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';

/** One change to what a list says about one fund. */
export type ListItemEdit =
  | { readonly kind: 'note'; readonly value: string | null }
  | { readonly kind: 'extra'; readonly key: string; readonly value: string | null }
  | { readonly kind: 'member'; readonly memberId: string | null }
  | { readonly kind: 'partner'; readonly investorId: string | null };

export interface FundListActions {
  readonly list: FundListDetail | undefined;
  readonly isPending: boolean;
  readonly error: string | null;
  editItem: (itemId: string, edit: ListItemEdit) => void;
  moveItem: (itemId: string, position: number) => void;
  reorder: (itemIds: readonly string[]) => void;
  removeItem: (itemId: string) => void;
  update: (changes: {
    name?: string;
    intro?: string | null;
    portfolioCompanyId?: string | null;
    columns?: readonly string[];
  }) => void;
  addColumn: (label: string) => void;
  removeColumn: (key: string) => void;
}

/**
 * A list and every write the list view makes. Each write answers with the list
 * as stored, which replaces the cached one, so the view redraws from what the
 * database now says rather than from a guess. Disabled with a null id, so the
 * funds grid can call it whichever half of the app it is showing.
 */
export function useFundList(listId: string | null): FundListActions {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const toast = useToast();
  const id = listId ?? '';
  const query = useQuery(trpc.lists.get.queryOptions({ id }, { enabled: listId !== null }));
  const key = trpc.lists.get.queryKey({ id });

  const settle = {
    onSuccess: (list: FundListDetail | null | undefined) => {
      if (list !== null && list !== undefined) queryClient.setQueryData(key, list);
      void queryClient.invalidateQueries({ queryKey: trpc.lists.list.queryKey() });
    },
    onError: (error: { message: string }) => {
      toast(error.message);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  };

  const updateItem = useMutation(trpc.lists.updateItem.mutationOptions(settle));
  const reorderItems = useMutation(trpc.lists.reorder.mutationOptions(settle));
  const remove = useMutation(trpc.lists.removeItem.mutationOptions(settle));
  const updateList = useMutation(trpc.lists.update.mutationOptions(settle));
  const addColumn = useMutation(
    trpc.lists.addColumn.mutationOptions({
      onSuccess: (answer) => settle.onSuccess(answer.list),
      onError: settle.onError,
    }),
  );
  const removeColumn = useMutation(trpc.lists.removeColumn.mutationOptions(settle));

  const editItem = useCallback(
    (itemId: string, edit: ListItemEdit) => {
      switch (edit.kind) {
        case 'note':
          updateItem.mutate({ itemId, note: edit.value });
          return;
        case 'extra':
          updateItem.mutate({ itemId, data: { [edit.key]: edit.value } });
          return;
        case 'member':
          updateItem.mutate({ itemId, suggestedMemberId: edit.memberId });
          return;
        case 'partner':
          updateItem.mutate({ itemId, suggestedInvestorId: edit.investorId });
          return;
      }
    },
    [updateItem],
  );

  return {
    list: query.data,
    isPending: listId !== null && query.isPending,
    error: query.error?.message ?? null,
    editItem,
    moveItem: (itemId, position) => updateItem.mutate({ itemId, position }),
    reorder: (itemIds) => reorderItems.mutate({ id, itemIds: [...itemIds] }),
    removeItem: (itemId) => remove.mutate({ itemId }),
    update: (changes) =>
      updateList.mutate({
        id,
        ...changes,
        columns: changes.columns === undefined ? undefined : [...changes.columns],
      }),
    addColumn: (label) => addColumn.mutate({ id, label }),
    removeColumn: (columnKey) => removeColumn.mutate({ id, key: columnKey }),
  };
}
